const LOG_LEVELS = ['debug', 'info', 'warn', 'error'];

const formatMeta = (meta) => {
  if (meta === undefined || meta === null) return '';
  if (typeof meta === 'string') return meta;
  try {
    return JSON.stringify(meta, (_, value) => (value instanceof Error ? value.message : value));
  } catch (err) {
    return String(meta);
  }
};

const createLogger = () => {
  const namespace = 'GMER';
  const history = [];

  const logAtLevel = (level, message, meta) => {
    const timestamp = new Date().toISOString();
    const entry = { timestamp, level, message, meta };
    history.push(entry);
    if (history.length > 500) history.shift();

    const formatted = `[${namespace}] [${timestamp}] [${level.toUpperCase()}] ${message}`;
    const payload = meta !== undefined ? formatMeta(meta) : undefined;

    if (level === 'debug' && !console.debug) {
      console.log(formatted, payload ?? '');
      return;
    }

    const writer = console[level] || console.log;
    if (payload !== undefined && payload !== '') {
      writer(formatted, payload);
    } else {
      writer(formatted);
    }
  };

  const logger = {
    history,
    log(level, message, meta) {
      if (!LOG_LEVELS.includes(level)) {
        logAtLevel('warn', `Unknown log level "${level}" for message "${message}".`, meta);
        return;
      }
      logAtLevel(level, message, meta);
    },
  };

  LOG_LEVELS.forEach((level) => {
    logger[level] = (message, meta) => logger.log(level, message, meta);
  });

  logger.flushHistory = () => {
    const snapshot = [...history];
    history.length = 0;
    return snapshot;
  };

  logger.getHistory = () => [...history];

  return logger;
};

export const logger = createLogger();

const isAxiosCancellation = (error) => (
  error?.code === 'ERR_CANCELED'
  || error?.name === 'CanceledError'
  || error?.message === 'canceled'
);

export const installGlobalErrorHandlers = () => {
  if (typeof window === 'undefined') return;
  window.appLog = logger;

  window.addEventListener('error', (event) => {
    logger.error('Global error event', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error?.stack || event.error?.message || event.error,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', {
      reason: event.reason?.stack || event.reason?.message || event.reason,
    });
  });
};

export const attachAxiosLogging = (axiosInstance) => {
  if (!axiosInstance || typeof axiosInstance.interceptors !== 'object') return;

  const requestTimes = new WeakMap();

  axiosInstance.interceptors.request.use((config) => {
    const enrichedConfig = { ...config };
    requestTimes.set(enrichedConfig, performance.now());
    logger.debug('HTTP request', {
      method: enrichedConfig.method?.toUpperCase(),
      url: enrichedConfig.url,
      params: enrichedConfig.params,
      data: enrichedConfig.data,
    });
    return enrichedConfig;
  }, (error) => {
    logger.error('HTTP request error', error);
    return Promise.reject(error);
  });

  axiosInstance.interceptors.response.use((response) => {
    const started = requestTimes.get(response.config);
    const durationMs = started ? Math.round(performance.now() - started) : undefined;
    logger.info('HTTP response', {
      method: response.config?.method?.toUpperCase(),
      url: response.config?.url,
      status: response.status,
      durationMs,
      dataPreview: Array.isArray(response.data)
        ? `array(${response.data.length})`
        : response.data && typeof response.data === 'object'
          ? Object.keys(response.data).slice(0, 5)
          : response.data,
    });
    return response;
  }, (error) => {
    const started = error?.config ? requestTimes.get(error.config) : undefined;
    const durationMs = started ? Math.round(performance.now() - started) : undefined;
    if (isAxiosCancellation(error)) {
      logger.debug('HTTP request canceled', {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        durationMs,
        message: error.message,
      });
      return Promise.reject(error);
    }
    logger.error('HTTP response error', {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      durationMs,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });
    return Promise.reject(error);
  });
};
